/** The embeddable chat widget: the floating bubble and its chat window. */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { KapiaWidgetConfig, SetPlacementArg, WebchatConfig } from './types';
import type { ChatBubble, MessageTemplate as WidgetMessageTemplate } from '../types';
import type { BrainWebchatApiClient } from './brainApi';
import type { BrainPreviewApiClient } from './brainPreviewApi';
import { resolveFontStack } from '../fonts';
import { useT } from '../i18n';
import { withAlpha } from '../colorUtils';
import { useWidgetChat } from './useWidgetChat';
import { useResize } from '../hooks/useResize';
import {
  resolveWidgetAnchor,
  parseRuntimeBottomOffset,
  type AnchorSide,
  type RuntimeBottomOffset,
  type SnapState,
} from '../anchor';
import { useVisitorSide } from '../hooks/useVisitorSide';
import { useAutoScroll } from '../hooks/useAutoScroll';

import { ChatButton } from '../components/ChatButton/ChatButton';
import { ChatBubbles } from '../components/ChatBubbles/ChatBubbles';
import { ChatWindowContainer } from '../components/ChatWindow/ChatWindowContainer';
import { ChatHeader } from '../components/ChatHeader/ChatHeader';
import { ChatContentContainer } from '../components/ChatContent/ChatContentContainer';
import { FAQInitialScreen } from '../components/FAQScreen/FAQInitialScreen';
import { ChatInput } from '../components/ChatInput/ChatInput';
import { ChatMessage } from '../components/ChatMessage/ChatMessage';
import { ThinkingIndicator } from '../components/ThinkingIndicator/ThinkingIndicator';
import { ResizeHandle } from '../components/ResizeHandle/ResizeHandle';
import { ConfirmationModal } from '../components/ConfirmationModal/ConfirmationModal';
import { DynamicFormDrawer } from '../components/DynamicFormDrawer/DynamicFormDrawer';
import { FormMessage } from '../components/FormMessage/FormMessage';
import { isFormSubmitted } from './formState';
import { FormSubmissionBubble } from '../components/FormSubmissionBubble/FormSubmissionBubble';
import { QuickActions } from '../components/QuickActions/QuickActions';
import { TopButton } from '../components/TopButton/TopButton';

/** Imperative handles the mounted `Widget` reports to `standalone/index.tsx` for the public API. */
export interface WidgetControls {
  /** Literally `setIsOpen`, whose updater form keeps `toggle()` right after UI clicks. */
  setOpen: Dispatch<SetStateAction<boolean>>;
  setPlacement: (placement: SetPlacementArg) => void;
}

interface WidgetProps {
  config: KapiaWidgetConfig;
  webchatConfig: WebchatConfig;
  apiClient: BrainWebchatApiClient | BrainPreviewApiClient;
  onClose?: () => void;
  /** When true, the widget is in preview mode (no localStorage, contained bounds) */
  previewMode?: boolean;
  containerRef?: React.RefObject<HTMLElement | null>;
  initialOpen?: boolean;
  onSizeChange?: (width: number, height: number) => void;
  /** Reports this instance's imperative handles once, right after mount. */
  onReady?: (controls: WidgetControls) => void;
}

export type { WidgetProps };

type ViewState = 'faq' | 'chat';

/**
 * sessionStorage flag mirroring the open state, so the widget reopens on the
 * next same-domain page only if it was open when the user left.
 */
const OPEN_STATE_KEY = 'kapia_webchat_open';

/** Scroll depth, as a fraction of the viewport height, past which the teaser bubbles hide. */
const BUBBLES_HIDE_SCROLL_RATIO = 0.5;

export function Widget({
  config,
  webchatConfig,
  apiClient,
  onClose,
  previewMode = false,
  containerRef,
  initialOpen = false,
  onSizeChange,
  onReady,
}: WidgetProps) {
  const t = useT();

  const hasSavedConversation = useMemo(() => {
    if (previewMode) return false;
    try {
      const apiKey = 'getApiKey' in apiClient ? (apiClient as { getApiKey(): string | null }).getApiKey() : null;
      const prefix = apiKey ? apiKey.slice(0, 12) : 'preview';
      return !!localStorage.getItem(`kapia_webchat_${prefix}`);
    } catch {
      return false;
    }
  }, [apiClient, previewMode]);

  // Keeps a user's close in effect across navigation.
  const [isOpen, setIsOpen] = useState(() => {
    if (initialOpen) return true;
    if (previewMode) return false;
    try {
      const persisted = sessionStorage.getItem(OPEN_STATE_KEY);
      if (persisted !== null) return persisted === 'open';
    } catch {
      // ignore storage access errors
    }
    return false;
  });
  const [view, setView] = useState<ViewState>(hasSavedConversation ? 'chat' : 'faq');

  useEffect(() => {
    if (previewMode) return;
    try {
      sessionStorage.setItem(OPEN_STATE_KEY, isOpen ? 'open' : 'closed');
    } catch {
      // ignore storage access errors
    }
  }, [isOpen, previewMode]);

  const [bubblesHidden, setBubblesHidden] = useState(false);
  useEffect(() => {
    if (previewMode) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      setBubblesHidden(window.scrollY > window.innerHeight * BUBBLES_HIDE_SCROLL_RATIO);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update(); // account for pages that load already scrolled
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [previewMode]);
  const [inputValue, setInputValue] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);

  const { messages, isStreaming, error, sendMessage, clearChat, activeForm, submittedFormIds, isRestoring, openForm, closeForm, submitForm, triggerForm, currentToolCall, quickActions, isQuickActionsLoading } = useWidgetChat({ apiClient, paramFields: webchatConfig.paramFields });

  const chatInputRef = useRef<HTMLInputElement>(null);

  const { containerRef: chatContainerRef, scrollToBottom, handleScroll: handleChatScroll } = useAutoScroll({
    // The chips too: on the Brain transport they arrive after the turn has
    // ended, so neither of the others changes when they do.
    deps: [messages, isStreaming, quickActions],
  });

  // Focus the chat input when streaming ends. Skip on mobile — auto-focusing there
  // pops up the on-screen keyboard every time the assistant finishes.
  const prevIsStreamingRef = useRef(false);
  useEffect(() => {
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
    if (prevIsStreamingRef.current && !isStreaming && isDesktop) {
      chatInputRef.current?.focus();
    }
    prevIsStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // Maximized to half the bounds. Geometry only — which side the widget
  // lives on is `visitorSide`, which outlives the maximize.
  const [snapState, setSnapState] = useState<SnapState>(null);
  const { visitorSide, commitVisitorSide } = useVisitorSide(previewMode);

  // Plain state: a change re-renders rather than remounts, so the open
  // conversation survives a `setPlacement()` call.
  const [runtimePlacement, setRuntimePlacement] = useState<{
    side: AnchorSide | null;
    bottomOffset: RuntimeBottomOffset | null;
  }>({ side: null, bottomOffset: null });

  // A host handler can call `setPlacement` every frame, so updates are batched
  // to one per animation frame; validation stays synchronous so a typo warns.
  const pendingPlacementRef = useRef<{ side: AnchorSide | null; bottomOffset: RuntimeBottomOffset | null }>({
    side: null,
    bottomOffset: null,
  });
  const placementRafRef = useRef<number | null>(null);

  const handleSetPlacement = useCallback(
    (placement: SetPlacementArg) => {
      if ('side' in placement && placement.side !== undefined) {
        if (placement.side === null || placement.side === 'left' || placement.side === 'right') {
          pendingPlacementRef.current.side = placement.side;
        } else {
          console.warn('KapiaWidget.setPlacement: invalid side, ignoring', placement.side);
        }
      }
      if ('bottomOffset' in placement && placement.bottomOffset !== undefined) {
        const parsed = parseRuntimeBottomOffset(placement.bottomOffset);
        if (parsed === 'invalid') {
          console.warn(
            'KapiaWidget.setPlacement: invalid bottomOffset, keeping the previous value',
            placement.bottomOffset,
          );
        } else {
          pendingPlacementRef.current.bottomOffset = parsed;
        }
      }
      if (placementRafRef.current === null) {
        placementRafRef.current = requestAnimationFrame(() => {
          placementRafRef.current = null;
          setRuntimePlacement({ ...pendingPlacementRef.current });
        });
      }
    },
    [],
  );

  useEffect(
    () => () => {
      if (placementRafRef.current !== null) cancelAnimationFrame(placementRafRef.current);
    },
    [],
  );

  useEffect(() => {
    onReady?.({ setOpen: setIsOpen, setPlacement: handleSetPlacement });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { size, bounds, windowWidth, handleMouseDown, collapseToDefault, positionLeftHalf, positionRightHalf } = useResize({
    initialWidthPercent: webchatConfig.defaultWidth ?? undefined,
    initialHeightPercent: webchatConfig.defaultHeight ?? undefined,
    bottomOffsetPercent: webchatConfig.bottomOffsetPercent,
    runtimeBottomOffset: runtimePlacement.bottomOffset,
    previewMode,
    containerRef,
  });
  const prevDimsRef = useRef({ w: webchatConfig.defaultWidth, h: webchatConfig.defaultHeight });
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!previewMode) return;
    const prev = prevDimsRef.current;
    const dimsChanged = prev.w !== webchatConfig.defaultWidth || prev.h !== webchatConfig.defaultHeight;
    if (dimsChanged) {
      prevDimsRef.current = { w: webchatConfig.defaultWidth, h: webchatConfig.defaultHeight };
    }
    // Collapse on first mount (to correct for container bounds) or when dims change
    if (!hasInitializedRef.current || dimsChanged) {
      hasInitializedRef.current = true;
      collapseToDefault();
    }
  }, [previewMode, webchatConfig.defaultWidth, webchatConfig.defaultHeight, collapseToDefault]);

  useEffect(() => {
    if (!onSizeChange) return;
    let boundsWidth = window.innerWidth;
    let boundsHeight = window.innerHeight;
    if (previewMode && containerRef?.current) {
      boundsWidth = containerRef.current.clientWidth;
      boundsHeight = containerRef.current.clientHeight;
    }
    const widthPct = boundsWidth > 0 ? Math.round((size.width / boundsWidth) * 100) : 0;
    const heightPct = boundsHeight > 0 ? Math.round((size.height / boundsHeight) * 100) : 0;
    onSizeChange(widthPct, heightPct);
  }, [size.width, size.height, onSizeChange, previewMode, containerRef]);

  // Re-measured on every resize, so it follows a rotation or window drag.
  const isMobile = windowWidth < 768;

  // Moving the window is the one explicit signal that the visitor wants a
  // side; it is also the only thing that writes one to storage.
  const handlePositionLeftHalf = () => {
    positionLeftHalf();
    setSnapState('left-half');
    commitVisitorSide('left');
  };

  const handlePositionRightHalf = () => {
    positionRightHalf();
    setSnapState('right-half');
    commitVisitorSide('right');
  };

  // Restores the size only: the visitor keeps the side they picked.
  const handleCollapseToDefault = () => {
    collapseToDefault();
    setSnapState(null);
  };

  const messageTemplates = useMemo(
    () =>
      (webchatConfig.messageTemplates || []).map((t) => ({
        label: t.label,
        search_query: t.message,
        emoji: t.icon,
        form_id: t.formId,
      })),
    [webchatConfig.messageTemplates],
  );

  const bubbles: ChatBubble[] = useMemo(
    () =>
      (webchatConfig.suggestions || []).map((s, i) => ({
        id: `bubble-${i}`,
        label: s.label,
        openChat: s.openChat,
        bgColor: s.bgColor ?? null,
        textColor: s.textColor ?? null,
      })),
    [webchatConfig.suggestions],
  );

  const handleTemplateClick = (template: WidgetMessageTemplate) => {
    setView('chat');
    if (template.form_id) {
      triggerForm(template.form_id);
    } else if (template.search_query) {
      sendMessage(template.search_query);
      scrollToBottom();
    }
  };

  const handleStartChat = () => {
    setView('chat');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    if (view === 'faq') {
      setView('chat');
    }
    sendMessage(inputValue.trim());
    setInputValue('');
    scrollToBottom();
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleBubbleClick = (bubble: ChatBubble) => {
    setIsOpen(true);
    if (bubble.openChat) return;
    setView('chat');
    const suggestion = webchatConfig.suggestions?.find((s) => s.label === bubble.label);
    if (suggestion?.formId) {
      triggerForm(suggestion.formId);
    } else if (suggestion?.searchQuery) {
      sendMessage(suggestion.searchQuery);
      scrollToBottom();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  // The mobile widget is fullscreen, so a `_self` link collapses it to show the
  // page. Never on `_blank`: that would persist as a deliberate close.
  const handleLinkNavigate = (target: '_blank' | '_self') => {
    if (!isMobile || target !== '_self') return;
    setIsOpen(false);
  };

  const handleNewChat = () => {
    setShowNewChatModal(true);
  };

  const handleConfirmNewChat = () => {
    setShowNewChatModal(false);
    clearChat();
    setView('faq');
    setInputValue('');
  };

  const handleCancelNewChat = () => {
    setShowNewChatModal(false);
  };


  // Where the widget sits: every branch reads the one resolver, so the closed
  // bubble, the open window and the preview cannot disagree.
  const anchor = useMemo(
    () =>
      resolveWidgetAnchor({
        isOpen,
        isMobile,
        previewMode,
        snapState,
        visitorSide,
        hostSide: config.position,
        hostOffsetX: config.offset?.x,
        hostOffsetY: config.offset?.y,
        runtimeSide: runtimePlacement.side,
        runtimeBottomOffset: runtimePlacement.bottomOffset,
        defaultSide: webchatConfig.defaultPosition,
        bottomOffsetPercent: webchatConfig.bottomOffsetPercent,
        boundsHeight: bounds.height,
      }),
    [
      isOpen,
      isMobile,
      previewMode,
      snapState,
      visitorSide,
      config.position,
      config.offset,
      runtimePlacement.side,
      runtimePlacement.bottomOffset,
      webchatConfig.defaultPosition,
      webchatConfig.bottomOffsetPercent,
      bounds.height,
    ],
  );

  const positionStyles: React.CSSProperties = useMemo(() => {
    const base: React.CSSProperties = previewMode
      ? { position: 'absolute', zIndex: 1 }
      : { position: 'fixed', zIndex: 9999 };

    // The mobile panel is 100vw/100dvh, so it pins to all four edges. This is
    // the only case that needs more than a bottom and one horizontal edge.
    if (anchor.fullBleed && isMobile) {
      return { ...base, top: 0, left: 0, right: 0, bottom: 0 };
    }

    return {
      ...base,
      bottom: anchor.bottomPx,
      ...(anchor.side === 'left' ? { left: anchor.insetPx } : { right: anchor.insetPx }),
    };
  }, [previewMode, isMobile, anchor]);

  const formThemeVars: React.CSSProperties = (() => {
    if (!webchatConfig.formBgColor) return {};
    const formText = webchatConfig.formTextColor || '#ffffff';
    return {
      ['--widget-form-bg' as string]: webchatConfig.formBgColor,
      ['--widget-form-text' as string]: formText,
      ['--widget-form-border' as string]: withAlpha(formText, 0.18),
      ['--widget-form-muted' as string]: withAlpha(formText, 0.7),
    };
  })();

  return (
    <div
      style={{
        ...positionStyles,
        ...(webchatConfig.roundedButtons === false
          ? ({ '--widget-button-radius': 'var(--widget-radius)' } as React.CSSProperties)
          : {}),
      }}
    >
      {isOpen && (
        <ChatWindowContainer
          id="kapia-chat-window"
          style={{
            width: isMobile ? '100vw' : size.width,
            height: isMobile ? '100dvh' : size.height,
            maxHeight: previewMode ? 'none' : '100vh',
            ...(webchatConfig.primaryColor ? { '--widget-primary': webchatConfig.primaryColor } as React.CSSProperties : {}),
            ...(webchatConfig.carouselImageAspect ? { '--carousel-image-aspect': webchatConfig.carouselImageAspect } as React.CSSProperties : {}),
            ...(webchatConfig.userMessageBgColor ? { '--widget-user-bubble': webchatConfig.userMessageBgColor } as React.CSSProperties : {}),
            ...(webchatConfig.userMessageTextColor ? { '--widget-user-text': webchatConfig.userMessageTextColor } as React.CSSProperties : {}),
            ...(webchatConfig.assistantMessageBgColor ? { '--widget-assistant-bubble': webchatConfig.assistantMessageBgColor } as React.CSSProperties : {}),
            ...(webchatConfig.assistantMessageTextColor ? { '--widget-assistant-text': webchatConfig.assistantMessageTextColor } as React.CSSProperties : {}),
            ...(resolveFontStack(webchatConfig.fontFamily) ? { '--widget-font-family': resolveFontStack(webchatConfig.fontFamily) } as React.CSSProperties : {}),
            ...(resolveFontStack(webchatConfig.secondaryFontFamily) ? { '--widget-font-family-secondary': resolveFontStack(webchatConfig.secondaryFontFamily) } as React.CSSProperties : {}),
            ...(webchatConfig.regularTextSize ? { '--widget-text-size-regular': `${webchatConfig.regularTextSize}px` } as React.CSSProperties : {}),
            ...(webchatConfig.specialTextSize ? { '--widget-text-size-special': `${webchatConfig.specialTextSize}px` } as React.CSSProperties : {}),
            ...(webchatConfig.quickActionFontSize ? { '--widget-text-size-quick-action': `${webchatConfig.quickActionFontSize}px` } as React.CSSProperties : {}),
            ...(webchatConfig.formTextSize ? { '--widget-form-text-size': `${webchatConfig.formTextSize}px` } as React.CSSProperties : {}),
            ...(webchatConfig.formButtonFontSize ? { '--widget-form-button-size': `${webchatConfig.formButtonFontSize}px` } as React.CSSProperties : {}),
            ...(webchatConfig.topButtonFontSize ? { '--widget-top-button-size': `${webchatConfig.topButtonFontSize}px` } as React.CSSProperties : {}),
            ...(webchatConfig.quickActionBgColor ? { '--widget-quick-action-bg': webchatConfig.quickActionBgColor } as React.CSSProperties : {}),
            ...(webchatConfig.quickActionTextColor ? { '--widget-quick-action-text': webchatConfig.quickActionTextColor } as React.CSSProperties : {}),
            ...formThemeVars,
          }}
        >
          {!isMobile && (
            <>
              {/* No handle on the anchored side: it could only shrink the panel. */}
              <ResizeHandle direction="n" onMouseDown={handleMouseDown} />
              <ResizeHandle
                direction={anchor.side === 'left' ? 'ne' : 'nw'}
                onMouseDown={handleMouseDown}
              />
              <ResizeHandle
                direction={anchor.side === 'left' ? 'e' : 'w'}
                onMouseDown={handleMouseDown}
              />
            </>
          )}

          <ChatHeader
            title={webchatConfig.agentName}
            onClose={handleClose}
            showNewChatButton={true}
            onNewChat={handleNewChat}
            showPositionMenu={!isMobile}
            onPositionLeftHalf={handlePositionLeftHalf}
            onPositionRightHalf={handlePositionRightHalf}
            onCollapseToDefault={handleCollapseToDefault}
          />

          {/* Waits for a restore, which is what knows whether the form was sent. */}
          {view === 'chat' && webchatConfig.topButton && !isRestoring && !submittedFormIds.has(webchatConfig.topButton.formId) && (
            <TopButton
              label={webchatConfig.topButton.label}
              onClick={() => triggerForm(webchatConfig.topButton!.formId, undefined, 'topButton')}
              primaryColor={webchatConfig.primaryColor ?? undefined}
              primaryHoverColor={webchatConfig.primaryHoverColor ?? undefined}
              useGradient={webchatConfig.useGradient}
              emoji={webchatConfig.topButton.emoji}
            />
          )}

          <ChatContentContainer>
            {view === 'faq' ? (
              <FAQInitialScreen
                greetingText={webchatConfig.greetingText}
                howCanWeHelpText={webchatConfig.howCanWeHelpText}
                messageTemplates={messageTemplates}
                logoUrl={webchatConfig.logoUrl ?? undefined}
                primaryColor={webchatConfig.primaryColor ?? undefined}
                primaryHoverColor={webchatConfig.primaryHoverColor ?? undefined}
                useGradient={webchatConfig.useGradient}
                onTemplateClick={handleTemplateClick}
                onStartChat={handleStartChat}
              />
            ) : (
              <div
                ref={chatContainerRef}
                onScroll={handleChatScroll}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  overflow: 'auto',
                  padding: '16px 16px 8px',
                  gap: '0',
                }}
              >
                {messages
                  .filter((msg) => msg.displayCarousel || msg.displayForm || msg.formSubmission || msg.content.trim() !== '')
                  .map((msg, index, filtered) => {
                    const isLast = index === filtered.length - 1;
                    return (
                      <div key={msg.id}>
                        {msg.formSubmission && (
                          <FormSubmissionBubble />
                        )}
                        {(msg.content.trim() !== '' || msg.displayCarousel) && (
                          <ChatMessage
                            role={msg.role}
                            content={msg.content}
                            displayCarousel={msg.displayCarousel}
                            isCarouselBuilding={msg.isCarouselBuilding}
                            previewMode={previewMode}
                            openLinksInNewTab={webchatConfig.openLinksInNewTab}
                            onLinkNavigate={handleLinkNavigate}
                            onCarouselButtonClick={(item, formId) => {
                              triggerForm(formId, item as unknown as Record<string, unknown>);
                            }}
                          />
                        )}
                        {msg.displayForm && (
                          <FormMessage
                            form={msg.displayForm}
                            isDisabled={false}
                            isSubmitted={isFormSubmitted(msg, submittedFormIds)}
                            onOpenForm={(form) => openForm(form, msg.id)}
                            primaryColor={webchatConfig.primaryColor ?? undefined}
                            primaryHoverColor={webchatConfig.primaryHoverColor ?? undefined}
                            useGradient={webchatConfig.useGradient}
                          />
                        )}
                        {msg.isCarouselBuilding && (
                          <ThinkingIndicator
                            currentToolCall={{
                              toolName: '',
                              displayName: '',
                              status: 'calling',
                              executionMessage: t('thinking.displaying'),
                            }}
                          />
                        )}
                        {isLast && !msg.displayForm && webchatConfig.quickActionsEnabled && (isQuickActionsLoading || (!isStreaming && quickActions.length > 0)) && (
                          <QuickActions
                            actions={quickActions}
                            isLoading={isQuickActionsLoading}
                            onActionClick={(action) => {
                              if (action.form_id) {
                                triggerForm(action.form_id);
                              } else if (action.search_query) {
                                sendMessage(action.search_query);
                                scrollToBottom();
                              }
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                {isStreaming && messages[messages.length - 1]?.content === '' && (
                  <ThinkingIndicator
                    currentToolCall={currentToolCall}
                    thinkingText={webchatConfig.thinkingText ?? undefined}
                  />
                )}
                {error && (
                  <div
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#fef2f2',
                      color: '#dc2626',
                      borderRadius: '8px',
                      fontSize: '14px',
                    }}
                  >
                    {error}
                  </div>
                )}
              </div>
            )}
          </ChatContentContainer>

          {view === 'chat' && (
            <ChatInput
              value={inputValue}
              onInputChange={setInputValue}
              onSubmit={handleSubmit}
              disabled={isStreaming}
              isLoading={isStreaming}
              primaryColor={webchatConfig.primaryColor ?? undefined}
              primaryHoverColor={webchatConfig.primaryHoverColor ?? undefined}
              useGradient={webchatConfig.useGradient}
              placeholder={webchatConfig.howCanWeHelpText || undefined}
              absolute={false}
              inputRef={chatInputRef}
            />
          )}

          <ConfirmationModal
            isOpen={showNewChatModal}
            title={t('confirm.newChatTitle')}
            description={t('confirm.newChatDescription')}
            onCancel={handleCancelNewChat}
            onConfirm={handleConfirmNewChat}
            primaryColor={webchatConfig.primaryColor ?? undefined}
            primaryHoverColor={webchatConfig.primaryHoverColor ?? undefined}
            useGradient={webchatConfig.useGradient}
          />

          <DynamicFormDrawer
            activeForm={activeForm}
            onClose={closeForm}
            onSubmit={submitForm}
            isDisabled={isStreaming}
            primaryColor={webchatConfig.primaryColor ?? undefined}
            primaryHoverColor={webchatConfig.primaryHoverColor ?? undefined}
            useGradient={webchatConfig.useGradient}
          />
        </ChatWindowContainer>
      )}

      {!isOpen && (
        <div
          style={
            {
              display: 'flex',
              flexDirection: 'column',
              // Two emissions of one value: `alignItems` lays out the bubble
              // stack and the button as siblings, while the custom property
              // reaches inside ChatBubbles to align the individual bubbles,
              // which are max-content wide and so ragged.
              alignItems: anchor.side === 'left' ? 'flex-start' : 'flex-end',
              '--widget-bubble-align': anchor.side === 'left' ? 'flex-start' : 'flex-end',
              gap: '8px',
            } as React.CSSProperties
          }
        >
          <ChatBubbles
            bubbles={bubbles}
            visible={!bubblesHidden}
            onBubbleClick={handleBubbleClick}
          />
          <ChatButton
            onClick={handleToggle}
            iconUrl={webchatConfig.iconUrl ?? undefined}
            size={webchatConfig.bubbleSize === 'small' ? 'small' : 'normal'}
          />
        </div>
      )}
    </div>
  );
}
